import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  clearControlBinding,
  controlSessionStore,
  setMidiBridgeConfig,
} from "../controls/controlSession";
import { MIDI_ALL_PORTS } from "../controls/midiBridge";
import { getLabActions } from "../labActions";
import { patchUi, uiStore } from "../store/uiStore";

const STORAGE_KEY = "audio-lab:settings-modal";
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 520;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 320;

type PanelLayout = { left: number; top: number; width: number; height: number };

function clampSize(width: number, height: number): { width: number; height: number } {
  const maxW = Math.max(MIN_WIDTH, window.innerWidth - 16);
  const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 16);
  return {
    width: Math.min(maxW, Math.max(MIN_WIDTH, Math.round(width))),
    height: Math.min(maxH, Math.max(MIN_HEIGHT, Math.round(height))),
  };
}

function clampPos(
  pos: { left: number; top: number },
  width: number,
  height: number,
): { left: number; top: number } {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - height - 8);
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

function defaultLayout(): PanelLayout {
  const size = clampSize(DEFAULT_WIDTH, DEFAULT_HEIGHT);
  return {
    ...clampPos(
      {
        left: Math.max(12, (window.innerWidth - size.width) / 2),
        top: Math.max(12, Math.min(64, window.innerHeight * 0.08)),
      },
      size.width,
      size.height,
    ),
    ...size,
  };
}

function loadLayout(): PanelLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultLayout();
    }
    const parsed = JSON.parse(raw) as Partial<PanelLayout>;
    const size = clampSize(
      typeof parsed.width === "number" ? parsed.width : DEFAULT_WIDTH,
      typeof parsed.height === "number" ? parsed.height : DEFAULT_HEIGHT,
    );
    const left = typeof parsed.left === "number" ? parsed.left : defaultLayout().left;
    const top = typeof parsed.top === "number" ? parsed.top : defaultLayout().top;
    return { ...clampPos({ left, top }, size.width, size.height), ...size };
  } catch {
    return defaultLayout();
  }
}

function saveLayout(layout: PanelLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

/**
 * Floating Settings panel — MIDI bridge + control bindings (Phase 1).
 */
export function SettingsModal() {
  const open = useStoreSelector(uiStore, s => s.settingsOpen);
  const session = useStoreSelector(controlSessionStore, s => s);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [layout, setLayout] = useState<PanelLayout>(() => loadLayout());
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  function close(): void {
    dragCleanupRef.current?.();
    setDragging(false);
    patchUi({ settingsOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    if ((event.target as HTMLElement | null)?.closest("button, input, select, label")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);
    const onMove = (moveEvent: PointerEvent): void => {
      setLayout(prev => {
        const pos = clampPos(
          { left: moveEvent.clientX - offsetX, top: moveEvent.clientY - offsetY },
          prev.width,
          prev.height,
        );
        const next = { ...prev, ...pos };
        saveLayout(next);
        return next;
      });
    };
    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragCleanupRef.current = undefined;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    dragCleanupRef.current = onUp;
  }

  if (!open) {
    return null;
  }

  const midi = session.prefs.midi;
  const prefix = midi.inputPrefix || "/midi";

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal settings-modal fixed z-50 flex flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)]${
        dragging ? " select-none" : ""
      }`}
      role="dialog"
      aria-label="Settings"
      style={{
        left: layout.left,
        top: layout.top,
        width: layout.width,
        height: layout.height,
      }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-1.5"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title text-[12px] font-semibold">Settings</span>
        <button
          type="button"
          className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
          onPointerDown={event => event.stopPropagation()}
          onClick={close}
        >
          Close
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto px-3 py-3">
        <section className="rounded-md border border-lab-line bg-lab-panel-deep p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <h2 className="text-[12px] font-semibold text-lab-text">MIDI Bridge</h2>
              <p className="text-[10px] text-lab-muted">
                Route Web MIDI onto the control bus ({prefix}/cc/…)
              </p>
            </div>
            <span className="rounded border border-lab-line px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-lab-muted">
              {session.midiStatus}
            </span>
          </div>

          <label className="mb-2 flex items-center justify-between gap-2 text-[11px]">
            <span>MIDI In</span>
            <input
              type="checkbox"
              checked={midi.enabled}
              onChange={event => setMidiBridgeConfig({ enabled: event.target.checked })}
            />
          </label>

          <div className="mb-2 grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-[10px] text-lab-muted">
              Input prefix
              <input
                className="rounded border border-lab-line bg-lab-bg px-2 py-1 text-[11px] text-lab-text"
                value={midi.inputPrefix}
                onChange={event => setMidiBridgeConfig({ inputPrefix: event.target.value })}
              />
            </label>
            <label className="grid gap-1 text-[10px] text-lab-muted">
              Input port
              <select
                className="rounded border border-lab-line bg-lab-bg px-2 py-1 text-[11px] text-lab-text"
                value={midi.inputDeviceId}
                onChange={event => setMidiBridgeConfig({ inputDeviceId: event.target.value })}
              >
                <option value={MIDI_ALL_PORTS}>All inputs</option>
                {session.midiInputs.map(port => (
                  <option key={port.id} value={port.id}>
                    {port.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="mb-3 grid gap-1 text-[10px] text-lab-muted">
            Channel filter
            <select
              className="rounded border border-lab-line bg-lab-bg px-2 py-1 text-[11px] text-lab-text"
              value={String(midi.channelFilter)}
              onChange={event =>
                setMidiBridgeConfig({ channelFilter: Number(event.target.value) })
              }
            >
              <option value="0">Omni (all channels)</option>
              {Array.from({ length: 16 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  Channel {i + 1}
                </option>
              ))}
            </select>
          </label>

          <label className="mb-2 flex items-center justify-between gap-2 text-[11px]">
            <span>MIDI Out</span>
            <input
              type="checkbox"
              checked={midi.outputEnabled}
              onChange={event => setMidiBridgeConfig({ outputEnabled: event.target.checked })}
            />
          </label>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-[10px] text-lab-muted">
              Output prefix
              <input
                className="rounded border border-lab-line bg-lab-bg px-2 py-1 text-[11px] text-lab-text"
                value={midi.outputPrefix}
                onChange={event => setMidiBridgeConfig({ outputPrefix: event.target.value })}
              />
            </label>
            <label className="grid gap-1 text-[10px] text-lab-muted">
              Output port
              <select
                className="rounded border border-lab-line bg-lab-bg px-2 py-1 text-[11px] text-lab-text"
                value={midi.outputDeviceId}
                onChange={event => setMidiBridgeConfig({ outputDeviceId: event.target.value })}
              >
                <option value={MIDI_ALL_PORTS}>All outputs</option>
                {session.midiOutputs.map(port => (
                  <option key={port.id} value={port.id}>
                    {port.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {session.midiError ? (
            <p className="mt-2 text-[11px] text-rose-300">{session.midiError}</p>
          ) : null}
          <p className="mt-2 text-[10px] text-lab-muted">
            Note on/off audition the open instrument (or last selected track). Enable MIDI In,
            then play your keyboard.
          </p>
        </section>

        <section className="rounded-md border border-lab-line bg-lab-panel-deep p-3">
          <h2 className="text-[12px] font-semibold text-lab-text">Bindings</h2>
          <p className="mb-2 text-[10px] text-lab-muted">
            Right-click Mixer / FX knobs → Learn. Mapping edits apply live.
          </p>
          {session.learnStatus ? (
            <p className="mb-2 text-[11px] text-lab-accent">{session.learnStatus}</p>
          ) : null}
          {session.bindings.length === 0 ? (
            <p className="text-[11px] text-lab-muted">No bindings yet.</p>
          ) : (
            <ul className="space-y-1">
              {session.bindings.map(entry => (
                <li
                  key={`${entry.target}:${entry.binding.path}`}
                  className="flex items-center justify-between gap-2 rounded border border-lab-line/80 bg-lab-bg px-2 py-1"
                >
                  <span className="min-w-0 truncate font-lab-mono text-[10px]">
                    {entry.target}
                    <span className="text-lab-muted"> ← </span>
                    {entry.binding.path}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded border border-lab-line px-1.5 py-0.5 text-[10px] text-lab-muted hover:border-rose-400/50 hover:text-rose-200"
                    onClick={() => {
                      clearControlBinding(entry.target);
                      getLabActions()?.setStatus?.(`Cleared binding for ${entry.target}`);
                    }}
                  >
                    Clear
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>,
    document.body,
  );
}
